import {
    tableMenuInsert,
    tableMenuInsertAbove,
    tableMenuInsertBelow,
    tableMenuInsertLeft,
    tableMenuInsertRight,
    tableMenuDelete,
    tableMenuDeleteTable,
    tableMenuDeleteColumn,
    tableMenuDeleteRow,
    tableMenuMerge,
    tableMenuMergeAbove,
    tableMenuMergeBelow,
    tableMenuMergeLeft,
    tableMenuMergeRight,
    tableMenuSplit,
    tableMenuSplitHorizontally,
    tableMenuSplitVertically,
    tableMenuStyle,
    tableMenuStyleDefault,
    tableMenuStyleGrid,
    tableMenuStyleLightLines,
    tableMenuStyleTwoTones,
    tableMenuStyleLightBands,
    tableMenuStyleClear,
    tableMenuHeaderRow,
} from './TableEditContextMenu.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';
import TableEditPlugin from '../plugin/TableEditPlugin';
import TableEditViewState from '../store/schema/TableEditViewState';
import setTableEditMenuViewState from '../actions/setTableEditMenuViewState';
import { PluginUIProps } from 'owa-editor/lib/store/schema/PluginWithUI';

import { ContextualMenu } from 'office-ui-fabric-react/lib/ContextualMenu';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { TableOperation } from 'roosterjs-editor-types';
import { computed } from 'mobx';
import { observer } from 'mobx-react';

@observer
export default class TableEditContextMenu extends React.Component<
    PluginUIProps<TableEditViewState, TableEditPlugin>,
    {}
> {
    render() {
        let { position } = this.props.viewState;
        return (
            position && (
                <ContextualMenu
                    shouldFocusOnMount={true}
                    target={position}
                    onDismiss={this.onDismiss}
                    items={this.menuItems}
                />
            )
        );
    }

    private onDismiss = () => {
        setTableEditMenuViewState(this.props.viewState, null, this.props.viewState.hasHeaders);
    };

    @computed
    private get menuItems(): IContextualMenuItem[] {
        return [
            {
                key: 'insert',
                name: loc(tableMenuInsert),
                subMenuProps: {
                    items: [
                        {
                            key: 'insertAbove',
                            name: loc(tableMenuInsertAbove),
                            data: TableOperation.InsertAbove,
                            onClick: this.editTable,
                        },
                        {
                            key: 'insertBelow',
                            name: loc(tableMenuInsertBelow),
                            data: TableOperation.InsertBelow,
                            onClick: this.editTable,
                        },
                        {
                            key: 'insertLeft',
                            name: loc(tableMenuInsertLeft),
                            data: TableOperation.InsertLeft,
                            onClick: this.editTable,
                        },
                        {
                            key: 'insertRight',
                            name: loc(tableMenuInsertRight),
                            data: TableOperation.InsertRight,
                            onClick: this.editTable,
                        },
                    ],
                },
            },
            {
                key: 'delete',
                name: loc(tableMenuDelete),
                subMenuProps: {
                    items: [
                        {
                            key: 'deleteTable',
                            name: loc(tableMenuDeleteTable),
                            data: TableOperation.DeleteTable,
                            onClick: this.editTable,
                        },
                        {
                            key: 'deleteColumn',
                            name: loc(tableMenuDeleteColumn),
                            data: TableOperation.DeleteColumn,
                            onClick: this.editTable,
                        },
                        {
                            key: 'deleteRow',
                            name: loc(tableMenuDeleteRow),
                            data: TableOperation.DeleteRow,
                            onClick: this.editTable,
                        },
                    ],
                },
            },
            {
                key: 'merge',
                name: loc(tableMenuMerge),
                subMenuProps: {
                    items: [
                        {
                            key: 'mergeAbove',
                            name: loc(tableMenuMergeAbove),
                            data: TableOperation.MergeAbove,
                            onClick: this.editTable,
                        },
                        {
                            key: 'mergeBelow',
                            name: loc(tableMenuMergeBelow),
                            data: TableOperation.MergeBelow,
                            onClick: this.editTable,
                        },
                        {
                            key: 'mergeLeft',
                            name: loc(tableMenuMergeLeft),
                            data: TableOperation.MergeLeft,
                            onClick: this.editTable,
                        },
                        {
                            key: 'mergeRight',
                            name: loc(tableMenuMergeRight),
                            data: TableOperation.MergeRight,
                            onClick: this.editTable,
                        },
                    ],
                },
            },
            {
                key: 'split',
                name: loc(tableMenuSplit),
                subMenuProps: {
                    items: [
                        {
                            key: 'splitHorizontally',
                            name: loc(tableMenuSplitHorizontally),
                            data: TableOperation.SplitHorizontally,
                            onClick: this.editTable,
                        },
                        {
                            key: 'splitVertically',
                            name: loc(tableMenuSplitVertically),
                            data: TableOperation.SplitVertically,
                            onClick: this.editTable,
                        },
                    ],
                },
            },
            {
                key: 'style',
                name: loc(tableMenuStyle),
                subMenuProps: {
                    items: [
                        {
                            key: 'styleDefault',
                            name: loc(tableMenuStyleDefault),
                            data: 'Default',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleGrid',
                            name: loc(tableMenuStyleGrid),
                            data: 'Grid',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleLightLines',
                            name: loc(tableMenuStyleLightLines),
                            data: 'LightLines',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleTwoTones',
                            name: loc(tableMenuStyleTwoTones),
                            data: 'TwoTones',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleLightBands',
                            name: loc(tableMenuStyleLightBands),
                            data: 'LightBands',
                            onClick: this.formatTable,
                        },
                        {
                            key: 'styleClear',
                            name: loc(tableMenuStyleClear),
                            data: 'Clear',
                            onClick: this.formatTable,
                        },
                    ],
                },
            },
            {
                key: 'headers',
                name: loc(tableMenuHeaderRow),
                isChecked: this.props.viewState.hasHeaders,
                canCheck: true,
                onClick: this.setTableHeaders,
            },
        ];
    }

    private setTableHeaders = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        this.onDismiss();
        this.props.plugin.setTableHeaders(this.props.viewState.hasHeaders);
    };

    private editTable = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        this.onDismiss();
        let operation = item.data as TableOperation;
        this.props.plugin.editTable(operation);
    };

    private formatTable = (ev: React.MouseEvent<HTMLElement>, item: IContextualMenuItem) => {
        this.onDismiss();
        let formatName = item.data as string;
        this.props.plugin.formatTable(formatName);
    };
}
